import React, { useEffect, useState } from 'react';
import { ScrollText, Loader2, Filter } from 'lucide-react';
import { useAdminStore } from '../../store/adminStore';

const ACTION_LABELS: Record<string, { label: string; cls: string }> = {
  ban: { label: 'Ban', cls: 'bg-error/10 text-error' },
  unban: { label: 'Unban', cls: 'bg-success/10 text-success' },
  warn: { label: 'Warn', cls: 'bg-warning/10 text-warning' },
  role_change: { label: 'Rôle', cls: 'bg-admin-accent/10 text-admin-accent' },
  wallet_credit: { label: 'Wallet', cls: 'bg-info/10 text-info' },
  refund: { label: 'Remboursement', cls: 'bg-success/10 text-success' },
  report_resolve: { label: 'Signalement', cls: 'bg-warning/10 text-warning' },
  appeal_review: { label: 'Appel', cls: 'bg-admin-accent/10 text-admin-accent' },
  server_ban: { label: 'Ban serveur', cls: 'bg-error/10 text-error' },
  server_unban: { label: 'Unban serveur', cls: 'bg-success/10 text-success' },
  announcement: { label: 'Annonce', cls: 'bg-info/10 text-info' },
};

const AdminAuditLog: React.FC = () => {
  const { auditEntries: auditLog, auditTotal: auditLogTotal, isLoadingAudit, fetchAuditLog } = useAdminStore();
  const [actionFilter, setActionFilter] = useState('');

  useEffect(() => { fetchAuditLog(0, actionFilter || undefined); }, [actionFilter]);

  const loadMore = () => {
    fetchAuditLog(auditLog.length, actionFilter || undefined);
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-5xl mx-auto p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-text-primary">Journal d&apos;audit</h2>
            <p className="text-xs text-text-muted mt-0.5">Historique de toutes les actions administratives</p>
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-text-muted" />
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="pl-9 pr-3 py-2 rounded-lg bg-bg-tertiary border border-border-color text-sm text-text-primary appearance-none cursor-pointer focus:outline-none focus:border-admin-accent/50 min-w-[160px]"
            >
              <option value="">Toutes les actions</option>
              {Object.entries(ACTION_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>
        </div>

        {isLoadingAudit && auditLog.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-5 w-5 animate-spin text-admin-accent" />
          </div>
        ) : auditLog.length === 0 ? (
          <div className="text-center py-16">
            <ScrollText className="h-10 w-10 text-text-muted/20 mx-auto mb-3" />
            <p className="text-sm text-text-muted">Aucune entrée dans le journal</p>
          </div>
        ) : (
          <>
            <div className="space-y-1">
              {auditLog.map((entry) => {
                const a = ACTION_LABELS[entry.action] || { label: entry.action, cls: 'bg-bg-tertiary text-text-secondary' };
                let details: Record<string, any> = {};
                try { details = typeof entry.details === 'string' ? JSON.parse(entry.details) : entry.details || {}; } catch {}
                return (
                  <div key={entry.id} className="flex items-start gap-3 px-4 py-3 bg-bg-secondary/40 rounded-xl hover:bg-bg-secondary/60 transition-colors">
                    <div className="flex-shrink-0 mt-0.5">
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${a.cls}`}>{a.label}</span>
                    </div>
                    <div className="flex-1 min-w-0 space-y-0.5">
                      <div className="flex items-center gap-1.5 text-sm">
                        <span className="font-medium text-admin-accent">{entry.admin_username}</span>
                        <span className="text-text-muted">→</span>
                        <span className="text-text-secondary">{entry.target_type} #{entry.target_id}</span>
                      </div>
                      {Object.keys(details).length > 0 && (
                        <p className="text-xs text-text-muted truncate">
                          {Object.entries(details).map(([k, v]) => `${k}: ${v}`).join(' · ')}
                        </p>
                      )}
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <p className="text-xs text-text-muted">{new Date(entry.created_at).toLocaleDateString('fr-CA')}</p>
                      <p className="text-[10px] text-text-muted/60">{new Date(entry.created_at).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            {auditLog.length < auditLogTotal && (
              <button
                onClick={loadMore}
                disabled={isLoadingAudit}
                className="flex items-center gap-1 mx-auto text-sm text-admin-accent hover:text-admin-accent-hover disabled:opacity-50"
              >
                {isLoadingAudit ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                Charger plus
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminAuditLog;
